import { WhatsApp, WhatsAppComponent } from 'domains/content/types';

/**
 * Transforms numeric placeholders ({{1}}, {{2}}) back to merge tag names ({{first_name}})
 * using the templateFields mapping from the API response.
 * @param text - The text containing numeric placeholders
 * @param templateFields - Mapping of numeric keys to merge tag names (e.g., {"1": "first_name"})
 * @returns The text with numeric placeholders replaced by merge tag names
 */
export const transformNumericPlaceholdersToMergeTags = (
    text: string,
    templateFields: Record<string, string>
): string => {
    if (!text || !templateFields || Object.keys(templateFields).length === 0) {
        return text;
    }

    // Replace all numeric placeholders like {{1}}, {{2}} with their corresponding merge tag names
    return text.replace(/\{\{(\d+)\}\}/g, (match, numericKey) => {
        const mergeTagName = templateFields[numericKey];
        return mergeTagName ? `{{${mergeTagName}}}` : match;
    });
};

/**
 * Extracts the text content from the BODY component of a WhatsApp template
 * and transforms numeric placeholders to readable merge tag names.
 * @param whatsAppData - The WhatsApp template data from the API
 * @param templateFields - Optional mapping of numeric keys to merge tag names
 * @returns The text content from the BODY component with readable merge tags, or empty string if not found
 */
export const getBodyComponentText = (
    whatsAppData: WhatsApp | { components?: WhatsAppComponent[] } | null,
    templateFields?: Record<string, string>
): string => {
    if (!whatsAppData?.components || !Array.isArray(whatsAppData.components)) {
        return '';
    }

    const bodyComponent = whatsAppData.components.find((component) => component.type === 'BODY');
    const rawText = bodyComponent?.text || '';

    // Transform numeric placeholders to merge tag names if templateFields is provided
    if (templateFields && Object.keys(templateFields).length > 0) {
        return transformNumericPlaceholdersToMergeTags(rawText, templateFields);
    }

    return rawText;
};
